import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const LAST_UPDATED = 'May 21, 2026';
const APP_NAME     = 'Solvi';
const APP_URL      = 'https://solvi.chat';
const CONTACT_EMAIL = 'support@solvi.chat';

export default function Privacy() {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-inner container">
          <Link to="/" className="legal-logo">{APP_NAME}</Link>
          <nav className="legal-nav">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy" className="active">Privacy Policy</Link>
          </nav>
        </div>
      </header>

      <main className="legal-main container">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

        <section>
          <p>
            {APP_NAME} ("we", "us", or "our") operates {APP_URL} and related services.
            This Privacy Policy explains how we collect, use, and protect information when you
            use our platform to build and deploy AI chatbots connected to social media channels.
          </p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>

          <h3>1.1 Account Information</h3>
          <p>When you register, we collect your name, email address, and a hashed password.</p>

          <h3>1.2 Facebook &amp; Instagram Data (via Meta OAuth)</h3>
          <p>When you connect a Facebook Page or Instagram Business account, we receive and store:</p>
          <ul>
            <li>Page access tokens (used to send and receive messages on your behalf)</li>
            <li>Page ID and page name</li>
            <li>Instagram Business Account ID and username (if linked to the connected Page)</li>
            <li>Webhook subscription details</li>
          </ul>
          <p>We use these solely to power your chatbot on the connected channel. We do not access
          your personal Facebook profile, friends list, or any data beyond what is necessary to
          operate the Messenger/Instagram messaging integration.</p>

          <h3>1.3 WhatsApp Business Data</h3>
          <p>When you connect a WhatsApp Business number via Embedded Signup, we store:</p>
          <ul>
            <li>Phone Number ID and WhatsApp Business Account (WABA) ID</li>
            <li>System access token for sending messages</li>
            <li>Display name / verified phone number</li>
          </ul>

          <h3>1.4 Conversation Data</h3>
          <p>Messages sent to your chatbot (including the end-user's messages and the AI-generated
          replies) are stored in our database so the chatbot can maintain conversation context.
          Conversations are associated with an anonymous session ID — we do not link conversation
          data to a real-world identity unless the end-user volunteers that information in their message.</p>

          <h3>1.5 Knowledge Base Content</h3>
          <p>Documents you upload (PDFs, DOCX, TXT) are stored and processed to generate embeddings
          used for retrieval-augmented generation. This content is visible only to your chatbot.</p>

          <h3>1.6 Usage Analytics</h3>
          <p>We track per-account message counts and token usage for billing and quota purposes.
          We do not sell this data to third parties.</p>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>To operate, maintain, and improve the Solvi platform</li>
            <li>To route incoming messages from Facebook Messenger, Instagram DM, and WhatsApp to your chatbot</li>
            <li>To generate AI responses using your configured knowledge base and system prompt</li>
            <li>To send those responses back to the end-user via the Meta API</li>
            <li>To monitor platform health, debug errors, and enforce usage limits</li>
            <li>To contact you about your account (transactional emails only)</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Sharing</h2>
          <p>We do <strong>not</strong> sell, rent, or trade your data. We share it only with:</p>
          <ul>
            <li><strong>Groq, Inc.</strong> — your messages are sent to the Groq API to generate AI responses. Groq's privacy policy applies to data processed through their API.</li>
            <li><strong>Hugging Face, Inc.</strong> — document text is sent to the Hugging Face Inference API to generate vector embeddings. Their privacy policy applies.</li>
            <li><strong>Meta Platforms, Inc.</strong> — access tokens and message content are exchanged with Meta's Graph API as required to operate the social channel integrations.</li>
            <li><strong>Hetzner Online GmbH</strong> — our hosting provider stores data on servers in Germany/Finland under EU data protection rules.</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Retention</h2>
          <ul>
            <li>Conversation messages: retained for 12 months, then automatically purged</li>
            <li>Access tokens: retained until you disconnect the channel or delete your account</li>
            <li>Knowledge base documents: retained until you delete them or your account</li>
            <li>Account data: retained until you delete your account</li>
          </ul>
        </section>

        <section>
          <h2>5. Facebook User Data Deletion</h2>
          <p>
            If you connected your Facebook account and later want all associated data removed,
            you can request deletion via Facebook's app settings:
            <strong> Settings &amp; Privacy → Settings → Apps and Websites → {APP_NAME} → Remove</strong>.
          </p>
          <p>
            When you remove the app, Facebook notifies us via a data deletion callback and we
            permanently delete all stored page tokens, channel records, and conversation history
            associated with your Facebook account within 30 days.
          </p>
          <p>
            You can also email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to request
            manual deletion. We will confirm completion within 30 days.
          </p>
          <p>
            To check the status of a deletion request, visit:{' '}
            <a href={`${APP_URL}/data-deletion`}>{APP_URL}/data-deletion</a>
          </p>
        </section>

        <section>
          <h2>6. Security</h2>
          <p>
            Access tokens are stored encrypted at rest. All data is transmitted over HTTPS/TLS.
            We use row-level access controls so each account can only access its own chatbots and
            channel data. Webhook payloads from Meta are verified using HMAC-SHA256 signatures.
          </p>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to or restrict certain processing</li>
            <li>Data portability</li>
          </ul>
          <p>To exercise these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
        </section>

        <section>
          <h2>8. Cookies</h2>
          <p>
            We use a single session cookie to keep you logged in. We do not use tracking or
            advertising cookies. The embedded chat widget uses <code>localStorage</code> to
            persist conversation session IDs across page reloads.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Solvi is not directed at children under 13. We do not knowingly collect personal
            information from children. If you believe we have inadvertently collected such data,
            contact us and we will delete it promptly.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this policy from time to time. When we do, we will update the
            "Last updated" date at the top. Continued use of the service after changes
            constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>11. Contact</h2>
          <p>
            Questions or concerns? Email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <p>© {new Date().getFullYear()} {APP_NAME}. <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link></p>
        </div>
      </footer>
    </div>
  );
}
